import React from 'react';
import moment from 'moment';
import { Image, Text, View, StyleSheet } from '@react-pdf/renderer';
import { colors, typography, spacing } from './styles/theme';
import { SectionTitle } from './components';
import icoUser from '../../img/ico_User.png';

const MAX_DISPLAY_POSTS = 100;

const styles = StyleSheet.create({
  container: {
    paddingBottom: spacing.lg,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  postCount: {
    fontSize: 10,
    color: colors.text.muted,
    backgroundColor: colors.background.section,
    paddingVertical: 4,
    paddingHorizontal: spacing.sm,
    borderRadius: 2,
  },
  postsContainer: {
    marginTop: spacing.sm,
  },
  post: {
    borderWidth: 1,
    borderColor: colors.border.light,
    borderStyle: 'solid',
    marginBottom: spacing.md,
    backgroundColor: colors.background.white,
  },
  postHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.sm,
    backgroundColor: colors.background.section,
    borderBottomWidth: 1,
    borderBottomColor: colors.border.light,
    borderBottomStyle: 'solid',
  },
  avatar: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: colors.background.table,
    marginRight: spacing.sm,
  },
  postAuthor: {
    flex: 1,
    fontSize: 9,
    fontWeight: 'bold',
    color: colors.text.primary,
    textTransform: 'capitalize',
  },
  postTimestamp: {
    fontSize: 8,
    color: colors.text.muted,
  },
  postBody: {
    padding: spacing.sm,
  },
  postQuestion: {
    fontSize: 10,
    color: colors.text.secondary,
    lineHeight: 1.4,
    borderLeftWidth: 3,
    borderLeftColor: colors.primary,
    borderLeftStyle: 'solid',
    paddingLeft: spacing.sm,
  },
  answersContainer: {
    marginTop: spacing.sm,
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border.subtle,
    borderTopStyle: 'solid',
  },
  answersTitle: {
    fontSize: 8,
    fontWeight: 'bold',
    color: colors.text.muted,
    textTransform: 'uppercase',
    marginBottom: spacing.xs,
  },
  answer: {
    flexDirection: 'row',
    paddingVertical: spacing.xs,
    paddingLeft: spacing.md,
    marginTop: spacing.xs,
    borderLeftWidth: 2,
    borderLeftColor: colors.accent,
    borderLeftStyle: 'solid',
  },
  answerAvatar: {
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: colors.background.table,
    marginRight: spacing.sm,
  },
  answerBody: {
    flex: 1,
  },
  answerAuthor: {
    fontSize: 8,
    fontWeight: 'bold',
    color: colors.text.primary,
    textTransform: 'capitalize',
  },
  answerText: {
    fontSize: 9,
    color: colors.text.secondary,
    lineHeight: 1.3,
    marginTop: 2,
  },
  answerTimestamp: {
    fontSize: 7,
    color: colors.text.muted,
    marginTop: 2,
  },
  truncationNotice: {
    ...typography.caption,
    fontStyle: 'italic',
    textAlign: 'center',
    marginTop: spacing.md,
    paddingVertical: spacing.sm,
    backgroundColor: colors.background.highlight,
  },
});

const Page7 = ({ forums = [] }) => {
  const displayPosts = forums.slice(0, MAX_DISPLAY_POSTS);
  const isTruncated = forums.length > MAX_DISPLAY_POSTS;

  // Count total items (posts + answers)
  const totalAnswers = forums.reduce(
    (sum, post) => sum + (post.answers?.length || 0),
    0
  );

  return (
    <View style={styles.container} break wrap>
      <View style={styles.headerRow}>
        <SectionTitle
          title="Preguntas y Respuestas"
          subtitle="Foro de consultas durante la transmisión"
          style={{ marginBottom: 0 }}
        />
        <Text style={styles.postCount}>
          {forums.length} pregunta{forums.length !== 1 ? 's' : ''} · {totalAnswers}{' '}
          respuesta{totalAnswers !== 1 ? 's' : ''}
        </Text>
      </View>

      <View style={styles.postsContainer} wrap>
        {displayPosts.map((post, index) => (
          <View key={post.id || index} style={styles.post} wrap={false}>
            {/* Post Header */}
            <View style={styles.postHeader}>
              <Image style={styles.avatar} src={icoUser} />
              <Text style={styles.postAuthor}>
                {post.author_name || 'Anónimo'}
              </Text>
              <Text style={styles.postTimestamp}>{post.dateLabel}</Text>
            </View>

            {/* Post Body */}
            <View style={styles.postBody}>
              <Text style={styles.postQuestion}>{post.message}</Text>

              {/* Answers */}
              {post.answers && post.answers.length > 0 && (
                <View style={styles.answersContainer}>
                  <Text style={styles.answersTitle}>
                    Respuestas ({post.answers.length})
                  </Text>
                  {post.answers.map((answer, answerIndex) => (
                    <View key={answerIndex} style={styles.answer}>
                      <Image style={styles.answerAvatar} src={icoUser} />
                      <View style={styles.answerBody}>
                        <Text style={styles.answerAuthor}>
                          {answer.author_name || 'Anónimo'}
                        </Text>
                        <Text style={styles.answerText}>{answer.message}</Text>
                        <Text style={styles.answerTimestamp}>
                          {moment
                            .unix(answer.timestamp)
                            .format('YYYY-MM-DD HH:mm:ss')}
                        </Text>
                      </View>
                    </View>
                  ))}
                </View>
              )}
            </View>
          </View>
        ))}
      </View>

      {isTruncated && (
        <Text style={styles.truncationNotice}>
          Se muestran las primeras {MAX_DISPLAY_POSTS} preguntas de un total de{' '}
          {forums.length}. El registro completo está disponible en la plataforma.
        </Text>
      )}
    </View>
  );
};

export default Page7;
